import { Component, EventEmitter, Input, Output } from '@angular/core';
import { AlertController, MenuController } from '@ionic/angular';

/**
 * Menú lateral con los espacios de trabajo (marcas) guardados.
 *
 * Es solo presentacional: lista los temas que recibe y avisa qué marca eligió
 * el usuario. Cada página decide qué hacer con esa selección, porque el
 * comportamiento difiere:
 *   - login: aplica el tema in situ (repinta el login sin navegar).
 *   - pre-login: aplica el tema y navega al login personalizado de esa marca.
 *
 * Uso:
 *   <app-brand-selection-menu #brandMenu
 *     menuId="loginMenu" contentId="login-content"
 *     [themes]="multiBrandThemesBundle.savedThemes"
 *     [activeThemeKey]="activeBrandThemeKey"
 *     (brandSelected)="..." (addWorkspace)="..." (brandRemoved)="...">
 *   </app-brand-selection-menu>
 *
 * Se abre/cierra desde la página con `@ViewChild` + `open()` / `close()`.
 */
@Component({
  selector: 'app-brand-selection-menu',
  templateUrl: './brand-selection-menu.component.html',
  styleUrls: ['./brand-selection-menu.component.scss'],
})
export class BrandSelectionMenuComponent {
  /** Temas guardados (salida de `getThemesSavedBundle().savedThemes`). */
  @Input() themes: any[] = [];
  /** Key del tema a resaltar como activo. `null` => ninguno. */
  @Input() activeThemeKey: string | null = null;
  /** Identificador del ion-menu; debe ser único por página. */
  @Input() menuId: string = 'brandSelectionMenu';
  /** Id del <ion-content> de la página que el menú desplaza. */
  @Input() contentId: string = '';

  /** Key del tema elegido. */
  @Output() brandSelected = new EventEmitter<string>();
  /** Click en "Agregar otro espacio de trabajo". */
  @Output() addWorkspace = new EventEmitter<void>();
  /**
   * Key del tema que el usuario confirmó quitar. El componente solo pregunta;
   * el borrado (storage, biometría, repintado) lo hace la página con
   * `LoginMultiModuloService.removeSavedTheme`.
   */
  @Output() brandRemoved = new EventEmitter<string>();

  /**
   * El ion-menu vive bajo un *ngIf para no registrar el gesto de borde cuando
   * el menú no está en uso (y para no existir siquiera si la página no lo abre).
   */
  isOpen: boolean = false;

  constructor(
    private menuCtrl: MenuController,
    private alertCtrl: AlertController,
  ) {}

  async open() {
    this.isOpen = true;
    // El ion-menu está detrás del *ngIf: hay que esperar a que exista en el DOM
    // antes de pedirle al MenuController que lo abra.
    setTimeout(() => {
      this.menuCtrl.open(this.menuId);
    }, 50);
  }

  async close() {
    try {
      await this.menuCtrl.close(this.menuId);
    } catch (error) {
      console.log(error);
    }
    this.isOpen = false;
  }

  async onBrandClick(themeKey: string) {
    await this.close();
    this.brandSelected.emit(themeKey);
  }

  /**
   * Pide confirmación antes de quitar un espacio: la acción es reversible
   * (se puede volver a agregar desde el pre-login) pero borra la credencial
   * biométrica de esa marca, así que no debe dispararse por un click al vuelo.
   *
   * El `stopPropagation` es imprescindible: el botón vive dentro de la tarjeta,
   * que tiene su propio `(click)` para seleccionar la marca.
   *
   * El menú NO se cierra: el usuario puede querer quitar varias seguidas. Si no
   * queda ninguna, lo cierra la página.
   */
  async onRemoveBrandClick(theme: any, event: Event) {
    event.stopPropagation();

    const workspaceName =  this.extractSubdomain(theme?.serviceBase) || theme?.titulo_login_app;

    const alert = await this.alertCtrl.create({
      // header: "Quitar espacio de trabajo",
      message: `¿Quitar <b>${workspaceName}</b> de tus espacios?`,
      mode: "ios",
      buttons: [
        { text: "Cancelar", role: "cancel" },
        {
          text: "Quitar",
          handler: () => {
            this.brandRemoved.emit(theme.key);
          },
        },
      ],
    });

    await alert.present();
  }

  async onAddWorkspaceClick() {
    await this.close();
    this.addWorkspace.emit();
  }

  extractSubdomain(backend: string): string {
    if (!backend) return '';

    // Extrae el dominio entre "https://gestiona." y "/api/"
    const match = backend.match(/https:\/\/gestiona\.([^\/]+)/);
    return match ? match[1] : '';
  }
}
